import React, { useState } from 'react';
import './tabsTable.sass';

/**
 * Componente de tabs para mostrar pocos stages de tablas de posiciones
 * Navegación horizontal con tabs clickeables para alternar entre contenidos
 * Se usa cuando hay 2 o menos stages para una mejor experiencia visual
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.stages - Array de stages/divisiones del torneo (máximo 2)
 * @param {string} props.stages[].id - ID único del stage
 * @param {string} props.stages[].name - Nombre del stage/división
 * @param {Function} props.renderTable - Función que renderiza la tabla para cada stage
 * @param {string} [props.variant="primary"] - Variante de estilo ("primary" para nivel superior, "nested" para nivel interior)
 * 
 * @returns {JSX.Element} Navegación por tabs con contenido dinámico
 */
export default function TabsTable({ stages, renderTable, variant = "primary", initialActiveTab = 0 }) {
  /**
   * Estado que controla qué tab está activa
   * Inicializado con initialActiveTab o 0 por defecto para mostrar el primer stage
   */
  const [activeTab, setActiveTab] = useState(initialActiveTab);

  /**
   * Maneja el cambio de tab activa
   * Actualiza el estado para mostrar el contenido correspondiente
   * 
   * @param {number} index - Índice de la tab seleccionada
   */
  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  return (
    <>
      {/* Navegación de tabs - Solo se muestra si hay más de 1 stage */}
      {stages.length > 1 && (
        <div 
          className={`tabs-component tabs-component--${variant}`}
          role="tablist"
          aria-label="Navegación de divisiones"
        >
          {stages.map((tab, index) => (
            <button
              key={`tab-${index}`}
              className={`tab tab--${variant} ${activeTab === index ? 'active' : ''}`}
              onClick={() => handleTabClick(index)}
              role="tab"
              aria-selected={activeTab === index}
              aria-controls={`tabpanel-${index}`}
              id={`tab-${index}`}
              type="button"
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {/* Contenido de la tab activa */}
      <div 
        className="tab-content"
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {stages[activeTab] && renderTable(stages[activeTab])}
      </div>
    </>
  );
}